import React, { useState } from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { ChevronDown, ChevronUp, Building2 } from 'lucide-react';

interface ActivityLog {
  id: string;
  user: {
    first_name: string;
    last_name: string;
  };
  action: string;
  entity_type: string;
  entity_id: string;
  details: {
    property_id?: string;
    property?: {
      id: string;
      name: string;
    };
    guest_first_name?: string;
    guest_last_name?: string;
  };
  created_at: string;
}

interface ActivityLogViewerProps {
  logs: ActivityLog[];
  initialLimit?: number;
}

export default function ActivityLogViewer({ logs, initialLimit = 5 }: ActivityLogViewerProps) {
  const [showAll, setShowAll] = useState(false);
  const displayedLogs = showAll ? logs : logs.slice(0, initialLimit);

  // Filtrer uniquement les logs concernant les réservations
  const bookingLogs = displayedLogs.filter(log => log.entity_type === 'booking');

  const getActionLabel = (action: string) => {
    switch (action) {
      case 'create':
        return 'Création';
      case 'update':
        return 'Modification';
      case 'delete':
        return 'Suppression';
      default:
        return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'create':
        return 'bg-green-100 text-green-800';
      case 'update':
        return 'bg-blue-100 text-blue-800';
      case 'delete':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (bookingLogs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucune activité à afficher
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {bookingLogs.map((log) => (
        <div key={log.id} className="bg-white rounded-lg shadow-sm p-4">
          <div className="flex items-center justify-between gap-2">
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
              {getActionLabel(log.action)}
            </span>
            <span className="text-sm text-gray-500">
              {format(new Date(log.created_at), 'Pp', { locale: fr })}
            </span>
          </div>

          <div className="mt-2 space-y-1">
            <p className="text-sm text-gray-900">
              {log.user.first_name} {log.user.last_name}
            </p>
            {log.details?.property?.name && (
              <div className="flex items-center gap-1.5 text-sm text-gray-600">
                <Building2 size={16} className="text-gray-400" />
                <span>{log.details.property.name}</span>
              </div>
            )}
          </div>
        </div>
      ))}

      {logs.length > initialLimit && (
        <button
          onClick={() => setShowAll(!showAll)}
          className="w-full flex items-center justify-center gap-2 py-2 text-sm text-blue-600 hover:text-blue-800 transition-colors"
        >
          {showAll ? (
            <>
              <ChevronUp size={16} />
              Voir moins
            </>
          ) : (
            <>
              <ChevronDown size={16} />
              Voir plus ({logs.length - initialLimit} activités)
            </>
          )}
        </button>
      )}
    </div>
  );
}